import { useEffect, useRef, useState, type ReactNode } from 'react'
import { CustomizationModeBar } from './CustomizationModeBar'
import { useNavigationEdit, type NavEditController } from './navigationEdit'
import type { NavDocument } from './navigationDocument'
import { useUnsavedChanges } from './unsavedChanges'

export interface NavigationEditSurfaceProps {
  base: NavDocument
  value: NavDocument | null
  scope: ReactNode
  note?: ReactNode
  busy?: boolean
  ready: boolean
  error?: unknown
  scrollClassName?: string
  countLabel: (visibleCount: number) => ReactNode
  excludedLabel: (excludedCount: number) => ReactNode
  renderNavigation: (controller: NavEditController) => ReactNode
  onSave: (document: NavDocument) => void
  onReset?: () => void
  onClose: () => void
}

/**
 * The data-free interaction shell around an app's own editable navigation bar.
 * Hosts retain their renderer, governed reads and writes, authority and copy.
 */
export function NavigationEditSurface({
  base,
  value,
  scope,
  note,
  busy = false,
  ready,
  error,
  scrollClassName,
  countLabel,
  excludedLabel,
  renderNavigation,
  onSave,
  onReset,
  onClose,
}: NavigationEditSurfaceProps) {
  const controller = useNavigationEdit({ base, value, busy })
  const [confirming, setConfirming] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const keepEditing = useRef<HTMLButtonElement>(null)
  const held = useRef(false)
  held.current = controller.held
  const dirty = useRef(false)
  dirty.current = controller.dirty

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || held.current) return
      const target = event.target instanceof Element ? event.target : null
      const nestedSurface = target?.closest('[role="dialog"], [role="menu"], [role="listbox"]') ?? null
      /* A phone drawer contains this surface and therefore yields Escape to it.
         A command dialog or portalled menu/listbox does not contain it and keeps
         its own Escape. The direction of this containment check is deliberate. */
      if (nestedSurface && !nestedSurface.contains(root.current)) return
      event.preventDefault()
      event.stopPropagation()
      if (dirty.current) setConfirming(true)
      else onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  useUnsavedChanges('navigation-editor', controller.dirty)
  useEffect(() => { if (confirming) keepEditing.current?.focus() }, [confirming])

  const leave = () => { if (controller.dirty) setConfirming(true); else onClose() }
  const status = error instanceof Error ? `Couldn’t save: ${error.message}`
    : !ready ? 'Loading governed navigation…'
      : controller.recoveryCount > 0
        ? `${controller.recoveryCount} ${controller.recoveryCount === 1 ? 'section needs' : 'sections need'} a group`
        : controller.excludedCount > 0 ? excludedLabel(controller.excludedCount) : undefined
  return (
    <>
      <div ref={root} className={scrollClassName} data-testid="nav-edit-mode">
        {renderNavigation(controller)}
      </div>
      <CustomizationModeBar
        scope={scope}
        count={countLabel(controller.visibleCount)}
        dirty={controller.dirty}
        busy={busy}
        status={status}
        statusTone={Boolean(error) || controller.recoveryCount > 0 ? 'error' : undefined}
        statusAction={controller.recoveryCount > 0 ? {
          label: 'Review unparented sections',
          onClick: () => root.current?.querySelector<HTMLElement>('#nav-recovery-section')?.focus(),
        } : undefined}
        note={note}
        confirmingDiscard={confirming}
        keepEditingRef={keepEditing}
        onKeepEditing={() => setConfirming(false)}
        onDiscard={onClose}
        onReset={onReset}
        onCancel={leave}
        saveDisabled={!ready || !controller.canSave || busy}
        onSave={() => onSave(controller.document())}
      />
    </>
  )
}
